import { EditorState, NodeSelection, Plugin } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { editableKey } from "../../../editor/plugins/editable";
import {
  Focus,
  selectionFocusKey
} from "../../../editor/plugins/selection-focus";
import { findParentNodeOfType } from "../../../util/selection";
import { InputScaleControlType } from "../schema";
import { InputScaleLabelNodeView } from "./input-scale-label-node-view";
import { InputScaleLabelsNodeView } from "./input-scale-labels-node-view";
import { InputScaleNodeView } from "./input-scale-node-view";
import { InputScaleValueNodeView } from "./input-scale-value-node-view";
import { inputScaleNodeViewKey, PluginState } from "./types";

function setValue(
  state: EditorState,
  value: (focused: Focus) => PluginState
): PluginState | null {
  const focused = selectionFocusKey.getState(state);

  if (!focused) {
    return null;
  } else {
    const { schema } = state;

    if (focused.node.type === schema.nodes.inputScale) {
      const { node } = focused;
      const controlType = node.attrs.controlType as InputScaleControlType;
      if (controlType === InputScaleControlType.dropdown) {
        return value(focused);
      } else {
        return null;
      }
    } else {
      const parent = findParentNodeOfType(schema.nodes.inputScale)(
        state.selection
      );

      if (parent) {
        const expectedFocused = {
          node: parent.node,
          pos: parent.pos
        };

        return value(expectedFocused);
      } else {
        return null;
      }
    }
  }
}

export function nodeViewPlugin() {
  return new Plugin<PluginState | null>({
    key: inputScaleNodeViewKey,
    state: {
      init(_config, state) {
        return setValue(state, (focused) => {
          return { opened: true, focused: focused };
        });
      },
      apply(_tr, value, _oldState, newState) {
        return setValue(newState, (focused) => {
          if (value != null && focused.node === value.focused.node) {
            const updatedValue =
              focused.pos === value.focused.pos
                ? { ...value }
                : { ...value, focused: focused };

            return updatedValue;
          } else {
            return { opened: true, focused: focused };
          }
        });
      }
    },
    props: {
      decorations: (state) => {
        const { doc } = state;

        const dropdownState = inputScaleNodeViewKey.getState(state);
        if (dropdownState == null) {
          return DecorationSet.empty;
        }

        const { focused, opened } = dropdownState;

        if (!opened) {
          return DecorationSet.empty;
        }

        const decorations = [
          Decoration.node(focused.pos, focused.pos + focused.node.nodeSize, {
            "data-dropdown-opened": ""
          })
        ];

        return DecorationSet.create(doc, decorations);
      },
      nodeViews: {
        inputScale: (node, view, getPos, decorations) => {
          return new InputScaleNodeView(
            node,
            view,
            getPos as () => number,
            decorations
          );
        },
        inputScaleValue: (node, view) => {
          return new InputScaleValueNodeView(node, view);
        },
        inputScaleNotApplicable: (node, view) => {
          return new InputScaleValueNodeView(node, view);
        },
        inputScaleLabels: (node) => {
          return new InputScaleLabelsNodeView(node);
        },
        inputScaleLabel: (node) => {
          return new InputScaleLabelNodeView(node);
        },
        inputScaleLabelNotApplicable: (node) => {
          return new InputScaleLabelNodeView(node);
        }
      },
      handleClickOn(view, _pos, node, nodePos, event, direct) {
        const { state } = view;

        const editable = editableKey.getState(state);
        if (editable && editable.focusable === false) {
          return false;
        }

        const { schema } = state;
        const target = event.target as Element;
        const isInContent = target.closest(".content") != null;
        const isSelectable =
          node.type.spec.selectable && node.type === schema.nodes.inputScale;

        if (
          (isSelectable && direct) ||
          (isSelectable && !direct && !isInContent)
        ) {
          const tr = state.tr.setSelection(
            new NodeSelection(state.doc.resolve(nodePos))
          );
          view.dispatch(tr);
          return true;
        } else {
          return false;
        }
      }
    }
  });
}
